import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaUserCheck, FaCalendarAlt, FaShoppingBag } from "react-icons/fa";
import CustomerHeader from "./CustomerHeader";
import CustomerFooter from "./CustomerFooter";
import { APP_LOGO_URL } from "../config/branding";
import "../pages/auth/LoginStyles.css";
import "../pages/auth/PremiumAuth.css";
import { TIMEZONE_OPTIONS } from "../constants/timezones";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

function FreelancerAccountForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [smsConsent, setSmsConsent] = useState(false);
  // A code typed at signup, remembered so the profile step can send it with the registration.
  const [typedCode, setTypedCode] = useState("");
  const [arrivedByLink] = useState(() => {
    try {
      return sessionStorage.getItem("ogestyle_referral_from_link") === "1";
    } catch (_err) {
      return false;
    }
  });
  const [storedCode] = useState(() => {
    try {
      return localStorage.getItem("ogestyle_referral_code") || "";
    } catch (_err) {
      return "";
    }
  });
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    timezone: "",
  });

  // The freelancer's timezone is deliberately NOT detected or pre-filled.
  //
  // Pre-selecting a value - even a correct detection - lets them submit without
  // ever making the choice, and hides a WRONG detection (travel, VPN, mismatched
  // locale) behind a value that looks plausible. The field is required and must be
  // actively chosen (policy 2026-10-03).

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (
      !/[A-Z]/.test(formData.password) ||
      !/[a-z]/.test(formData.password) ||
      !/\d/.test(formData.password) ||
      !/[^A-Za-z0-9]/.test(formData.password)
    ) {
      newErrors.password =
        "Password must include uppercase, lowercase, number, and special character";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.timezone) {
      newErrors.timezone = "Please choose your timezone";
    }

    if (!smsConsent) {
      newErrors.smsConsent = "Please agree to receive SMS notifications";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Never submit without an actively chosen timezone (browser guard, not backend).
    if (!formData.timezone || !formData.timezone.trim()) {
      setErrors({ submit: "Please choose your timezone." });
      return;
    }

    if (!validate()) return;

    setLoading(true);
    setErrors({});

    try {
      const response = await axios.post(`${API_BASE_URL}/api/owners/register`, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        timezone: formData.timezone,
        smsConsent: smsConsent,
        userType: "freelancer", // Important: tells backend to send freelancer verification email
      });

      if (
        response.data.success ||
        response.data.status === "success" ||
        response.data.message.includes("verification")
      ) {
        // Hand the code to the profile step, which is where the attribution is written.
        if (typedCode.trim()) {
          try {
            localStorage.setItem("ogestyle_referral_code", typedCode.trim().toUpperCase());
          } catch (_err) {
            /* storage disabled: they can still type it on the next screen */
          }
        }
        // Show success message and redirect to check email page
        navigate("/freelancer/verify-email", {
          state: {
            email: formData.email,
            message:
              "Check your email to verify your account, then you can complete your freelancer profile.",
          },
        });
      }
    } catch (error) {
      // User-friendly error messages
      let message = "Registration failed. Please try again.";

      if (error.response?.data?.message) {
        const serverMessage = error.response.data.message;

        // Make common errors more user-friendly
        if (
          serverMessage.toLowerCase().includes("already exists") ||
          serverMessage.toLowerCase().includes("duplicate")
        ) {
          message =
            "This email address is already registered. Please login or use a different email.";
        } else if (serverMessage.toLowerCase().includes("password")) {
          message = serverMessage; // Password validation messages are already clear
        } else if (serverMessage.toLowerCase().includes("email")) {
          message = "Please provide a valid email address.";
        } else {
          message = serverMessage;
        }
      } else if (error.response?.status === 500) {
        message =
          "Server error. Please try again in a few moments or contact support if the issue persists.";
      } else if (error.message === "Network Error") {
        message =
          "Unable to connect to server. Please check your internet connection.";
      }

      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomerHeader />
      <div className="login-page og-premium-auth" style={{ minHeight: "calc(100vh - 50px)" }}>
        {/* Left Panel - Branding */}
        <div className="login-branding">
          <div className="og-branding">
            <p className="og-eyebrow">For independent professionals</p>
            <h2 className="og-brand-title">
              Build your own beauty business
            </h2>
            <p className="og-brand-sub">
              Your own dashboard, a discoverable profile, and booking, products
              &amp; payments — everything you need to grow, without a shop.
            </p>
            <div className="og-features">
              <div className="og-feature">
                <span className="og-feature-ic"><FaUserCheck /></span>
                <span>Get clients without a shop</span>
              </div>
              <div className="og-feature">
                <span className="og-feature-ic"><FaCalendarAlt /></span>
                <span>Control your schedule &amp; rates</span>
              </div>
              <div className="og-feature">
                <span className="og-feature-ic"><FaShoppingBag /></span>
                <span>Sell beauty products to boost income</span>
              </div>
            </div>
            <div className="og-trust">
              <span>Own dashboard &amp; insights</span>
              <span>No per-seat fees</span>
              <span>No contract</span>
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="login-form-panel">
          <div className="login-container">
            <div className="login-header">
              <div className="mobile-logo"><img src={APP_LOGO_URL} alt="OGESTYLE" style={{ maxWidth: "120px", height: "auto" }} /></div>
              <h2>Become a Freelancer</h2>
              <p className="subtitle">
                Create your account to start your beauty business journey
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div
                className="form-row"
                style={{ display: "flex", gap: "16px" }}
              >
                <div
                  className="form-group"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Enter your first name"
                    className={errors.firstName ? "error" : ""}
                  />
                  {errors.firstName && (
                    <span className="error-text">{errors.firstName}</span>
                  )}
                </div>

                <div
                  className="form-group"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter your last name"
                    className={errors.lastName ? "error" : ""}
                  />
                  {errors.lastName && (
                    <span className="error-text">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className={errors.email ? "error" : ""}
                />
                {errors.email && (
                  <span className="error-text">{errors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(123) 456-7890"
                  className={errors.phone ? "error" : ""}
                />
                {errors.phone && (
                  <span className="error-text">{errors.phone}</span>
                )}
              </div>

              {/* SMS Consent Checkbox (TCPA Compliance) */}
              <div
                style={{
                  marginTop: "24px",
                  padding: "20px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "12px",
                  border: "1px solid #e9ecef",
                }}
              >
                <label
                  htmlFor="smsConsent"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#1f2937",
                  }}
                >
                  <input
                    type="checkbox"
                    id="smsConsent"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    required
                    style={{
                      marginTop: "3px",
                      width: "18px",
                      height: "18px",
                      flexShrink: 0,
                      cursor: "pointer",
                      accentColor: "#1a659e",
                    }}
                  />
                  <span>
                    I agree to receive SMS notifications from OGESTYLE about my
                    appointments, including booking confirmations, reminders,
                    and updates. Message frequency may vary. Message and data
                    rates may apply. Text HELP for support. Reply STOP to
                    opt-out anytime.{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1a659e", textDecoration: "underline" }}
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
                {errors.smsConsent && (
                  <span
                    className="error-text"
                    style={{ marginTop: "8px", display: "block" }}
                  >
                    {errors.smsConsent}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="timezone">Your Timezone *</label>
                <select
                  id="timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleChange}
                  required
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    fontSize: "15px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "12px",
                    transition: "all 0.2s ease",
                    boxSizing: "border-box",
                    background: "#f8fafc",
                  }}
                >
                  <option value="">Select your timezone...</option>
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
                <p
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    marginTop: "6px",
                    margin: "6px 0 0 0",
                  }}
                >
                  Please choose your timezone for accurate scheduling.
                </p>
              </div>

              <div
                className="form-row"
                style={{ display: "flex", gap: "16px" }}
              >
                <div
                  className="form-group"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <label htmlFor="password">Password *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create a strong password"
                    className={errors.password ? "error" : ""}
                  />
                  {errors.password && (
                    <span className="error-text">{errors.password}</span>
                  )}
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      marginTop: "6px",
                      margin: "6px 0 0 0",
                    }}
                  >
                    8+ chars with upper, lower, number & special char
                  </p>
                </div>

                <div
                  className="form-group"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <label htmlFor="confirmPassword">Confirm Password *</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter your password"
                    className={errors.confirmPassword ? "error" : ""}
                  />
                  {errors.confirmPassword && (
                    <span className="error-text">{errors.confirmPassword}</span>
                  )}
                </div>
              </div>

              {/*
                Asked HERE because this is the first screen a freelancer sees. The code is stored and
                sent with the profile step, which is where the attribution is written. Hidden when they
                arrived through an agent's link (that code already wins) or when one is already stored.
              */}
              {!arrivedByLink && !storedCode && (
                <div className="form-group" style={{ marginBottom: "20px" }}>
                  <label htmlFor="signupCode">Referral or promo code</label>
                  <input
                    id="signupCode"
                    name="signupCode"
                    type="text"
                    value={typedCode}
                    onChange={(e) => setTypedCode(e.target.value.toUpperCase())}
                    placeholder="Leave blank if you have none"
                  />
                  <small> A promotion code, or the code of the person who referred you. </small>
                </div>
              )}

              {errors.submit && (
                <div
                  style={{
                    padding: "12px",
                    background: "#ffebee",
                    border: "1px solid #ff4444",
                    borderRadius: "8px",
                    marginBottom: "20px",
                    color: "#ff4444",
                    fontSize: "14px",
                  }}
                >
                  {errors.submit}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "14px",
                  background: loading ? "#a9bdd1" : "#183a63",
                  color: "white",
                  border: "none",
                  borderRadius: "999px",
                  fontSize: "16px",
                  fontWeight: "700",
                  fontFamily: "'DM Sans', Arial, sans-serif",
                  cursor: loading ? "not-allowed" : "pointer",
                  boxShadow: "0 9px 20px rgba(24, 58, 99, 0.18)",
                  transition: "background 0.2s ease, transform 0.15s ease",
                }}
                onMouseOver={(e) =>
                  !loading && (e.target.style.transform = "translateY(-2px)")
                }
                onMouseOut={(e) => (e.target.style.transform = "translateY(0)")}
              >
                {loading ? "Creating Account..." : "Continue →"}
              </button>

              <p
                style={{
                  textAlign: "center",
                  marginTop: "24px",
                  fontSize: "14px",
                  color: "#666",
                }}
              >
                Already have an account?{" "}
                <a
                  href="/login?redirect=/freelancer/profile"
                  style={{
                    color: "#1a659e",
                    textDecoration: "none",
                    fontWeight: "600",
                  }}
                >
                  Sign In
                </a>
              </p>
            </form>
          </div>
        </div>
      </div>
      <CustomerFooter />
    </>
  );
}

export default FreelancerAccountForm;
